import { create } from 'zustand';

/**
 * calendarShellStore (calendar-shell-resize — 2026-07-24) — 캘린더 3열 셸의
 * 좌측 사이드바(미니 캘린더 바)와 우측 선택일 레일의 **폭**을 관리한다.
 * 둘 다 localStorage 로 영속(새로고침·뷰 전환·재방문에도 유지).
 *
 * additive — 기존 calendarStore(뷰·표시 캘린더) 와 무관한 별도 store 이며,
 * 저장값이 없을 때의 기본값은 CalendarShell.css 의 기존 고정 폭과 동일해서
 * 한 번도 드래그하지 않은 사용자에게는 변경 전과 완전히 같은 레이아웃이 나온다.
 *
 * 폭 적용 경로: CalendarPage 가 `--cal-side-w` / `--cal-rail-w` CSS 변수로 셸 루트에
 * 주입하고, CalendarShell.css 의 `.calendar-page--shell .cal-side{width:var(--cal-side-w,236px)}`
 * 규칙이 읽는다(기존 `.cal-side{width:236px}` 규칙은 그대로 fallback 으로 남는다).
 *
 * chat-sidebar-resize-media(chatSidebarStore) / chat-doc-preview-resize 와 동일 패턴.
 */

/** 좌측 사이드바(미니 캘린더 바) — 최소/최대/기본 폭(px). 기본값 = 기존 CSS 고정폭. */
export const CAL_SIDE_MIN_WIDTH = 190;
export const CAL_SIDE_MAX_WIDTH = 460;
export const CAL_SIDE_DEFAULT_WIDTH = 236;

/** 우측 선택일 레일(상세 일정 바) — 최소/최대/기본 폭(px). 기본값 = 기존 CSS 고정폭. */
export const CAL_RAIL_MIN_WIDTH = 180;
export const CAL_RAIL_MAX_WIDTH = 520;
export const CAL_RAIL_DEFAULT_WIDTH = 224;

const LS_SIDE_WIDTH = 'calendar.shell.sideWidth';
const LS_RAIL_WIDTH = 'calendar.shell.railWidth';

function clamp(w: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(w)));
}

/** 저장값 로드 — 비정상/미저장/localStorage 비활성이면 기본 폭(=기존 고정폭). */
function readWidth(key: string, def: number, min: number, max: number): number {
  try {
    const v = Number(localStorage.getItem(key));
    return Number.isFinite(v) && v > 0 ? clamp(v, min, max) : def;
  } catch {
    return def;
  }
}

function persist(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* localStorage 비활성(프라이빗 모드 등) — 무시, 세션 내 동작은 유지 */
  }
}

interface CalendarShellState {
  /** 좌측 사이드바 폭(px). */
  sideWidth: number;
  /** 우측 선택일 레일 폭(px). */
  railWidth: number;
  /** 범위 clamp 후 저장. 드래그 중 매 mousemove 마다 호출된다. */
  setSideWidth: (w: number) => void;
  setRailWidth: (w: number) => void;
  /** 기본 폭 복귀(핸들 더블클릭). */
  resetSideWidth: () => void;
  resetRailWidth: () => void;
}

export const useCalendarShellStore = create<CalendarShellState>((set) => ({
  sideWidth: readWidth(LS_SIDE_WIDTH, CAL_SIDE_DEFAULT_WIDTH, CAL_SIDE_MIN_WIDTH, CAL_SIDE_MAX_WIDTH),
  railWidth: readWidth(LS_RAIL_WIDTH, CAL_RAIL_DEFAULT_WIDTH, CAL_RAIL_MIN_WIDTH, CAL_RAIL_MAX_WIDTH),
  setSideWidth: (w) => {
    const next = clamp(w, CAL_SIDE_MIN_WIDTH, CAL_SIDE_MAX_WIDTH);
    persist(LS_SIDE_WIDTH, String(next));
    set({ sideWidth: next });
  },
  setRailWidth: (w) => {
    const next = clamp(w, CAL_RAIL_MIN_WIDTH, CAL_RAIL_MAX_WIDTH);
    persist(LS_RAIL_WIDTH, String(next));
    set({ railWidth: next });
  },
  resetSideWidth: () => {
    persist(LS_SIDE_WIDTH, String(CAL_SIDE_DEFAULT_WIDTH));
    set({ sideWidth: CAL_SIDE_DEFAULT_WIDTH });
  },
  resetRailWidth: () => {
    persist(LS_RAIL_WIDTH, String(CAL_RAIL_DEFAULT_WIDTH));
    set({ railWidth: CAL_RAIL_DEFAULT_WIDTH });
  },
}));
